/*
 * The Thermal & Metabolic tab's time series (docs/FIELD_AI_VISION_SPEC.md
 * section 43, phase C3): one ROI label's temperature over capture time, from
 * the captures GET /experiments/{id}/thermal lists -- each with its current
 * analysis, whose ROI values are the server's.
 *
 *   roiLabels(captures)                         every ROI label, in first-seen order
 *   buildSeries(captures, units, {label, metric, by})
 *       metric  't_avg'  the ROI's mean (°C)
 *               'delta'  its difference from the analysis's reference (°C);
 *                        a capture analysed with no reference gives no point
 *       by      'unit'   one line per unit the capture is linked to
 *               'arm'    one line per arm: per day (UTC), the mean of its
 *                        replicates' values, with their range (lo, hi)
 *       A capture linked to the experiment but to no unit is its own line
 *       (key EXPERIMENT_KEY) either way.
 *       An ROI saved as measuring a unit (0102, roi.unit) counts for that
 *       unit only; one saved before, or tied to none, for every unit its
 *       capture is linked to. `label` UNIT_ROIS takes every ROI tied to a
 *       unit whatever its label (each unit's own region of a shared tray);
 *       hasUnitRois(captures) says whether there are any. A unit line has
 *       one point per capture.
 *       -> [{key, name, points: [{t, value, lo, hi, n, captureIds, lids}]}]
 *          points in time order; t in ms since 1970.
 *   lidStates(series)                           the lid states the points were taken with
 *
 * Per target (plan phase 10): one record's heat profile over time, from ROIs
 * tied to it -- each ROI names its target {kind, id, code} (0102).
 *
 *   targetRows(captures)                        the targeted ROIs of each capture's current
 *                                               analysis, as rows of the dossier's thermal_rois
 *   targetSeries(rows, {metric, target})
 *       rows    GET /specimens/{id}/dossier's thermal_rois, or targetRows(captures)
 *       metric  't_avg', 't_min', 't_max' (°C), or 'delta': delta_avg_c, the ROI's
 *               mean against its analysis's reference (no reference, no point)
 *       target  'kind:id' to keep one line only
 *       -> [{key: 'kind:id', kind, id, code, points: [{t, value, lo, hi, n, captureIds, labels, lids}]}]
 *          one line per target, in TARGET_ORDER then code; one point per capture:
 *          several ROIs of one capture on one target are their mean, with their
 *          range (lo, hi) and how many (n). An ROI with no target -- saved before
 *          0102, or `redacted` to this reader -- is in no line.
 *
 * Pure: no React, no request.
 */
export const EXPERIMENT_KEY = '_experiment';
/** buildSeries' `label` for "each unit's own ROI": every ROI saved as measuring a unit (0102), whatever its label. */
export const UNIT_ROIS = '_units';
const DAY = 24 * 60 * 60 * 1000;

const same = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

export function captureTime(capture) {
  const t = Date.parse(capture?.captured_at || capture?.created_at || '');
  return Number.isFinite(t) ? t : null;
}

export function roiLabels(captures) {
  const seen = [];
  for (const c of captures || []) {
    for (const r of c.analysis?.rois || []) {
      // A redacted ROI (an outline to this reader) has no label.
      if (r.label && !seen.some((l) => same(l, r.label))) seen.push(r.label);
    }
  }
  return seen;
}

function valueOf(roi, metric) {
  const v = metric === 'delta' ? roi.delta_avg_c : roi.t_avg_c;
  return Number.isFinite(v) ? v : null;
}

/** Whether any capture has an ROI saved as measuring one unit (0102): the UNIT_ROIS choice is offered. */
export function hasUnitRois(captures) {
  return (captures || []).some((c) => (c.analysis?.rois || []).some((r) => r.unit?.unit_id));
}

export function buildSeries(captures, units, { label, metric = 't_avg', by = 'unit' } = {}) {
  const byId = new Map((units || []).map((u) => [u.unit_id, u]));
  const raw = [];                                    // [{key, name, t, value, captureId, lid}]
  const point = (u, t, value, captureId, lid) => raw.push(by === 'arm'
    ? { key: `arm:${u.arm}`, name: u.arm, t, value, captureId, lid, day: Math.floor(t / DAY) }
    : { key: `unit:${u.unit_id}`, name: u.label, t, value, captureId, lid });
  for (const c of captures || []) {
    const t = captureTime(c);
    if (t === null) continue;
    const rois = c.analysis?.rois || [];
    const picked = label === UNIT_ROIS ? rois.filter((r) => r.unit?.unit_id) : rois.filter((r) => same(r.label, label));
    const lid = c.lid_state || null;
    for (const roi of picked) {
      const value = valueOf(roi, metric);
      if (value === null) continue;
      // An ROI saved as measuring a unit is that unit's only (another
      // experiment's unit is no line here); one saved before 0102, or tied to
      // none, stands for every unit its capture is linked to.
      if (roi.unit?.unit_id) {
        const own = byId.get(roi.unit.unit_id);
        if (own) point(own, t, value, c.capture_id, lid);
        continue;
      }
      const linked = (c.unit_ids || []).map((id) => byId.get(id)).filter(Boolean);
      if (!linked.length) {
        raw.push({ key: EXPERIMENT_KEY, name: null, t, value, captureId: c.capture_id, lid });
        continue;
      }
      for (const u of linked) point(u, t, value, c.capture_id, lid);
    }
  }
  const lines = new Map();
  for (const p of raw) {
    if (!lines.has(p.key)) lines.set(p.key, { key: p.key, name: p.name, raw: [] });
    lines.get(p.key).raw.push(p);
  }
  const out = [];
  for (const line of lines.values()) {
    let points;
    if (line.key.startsWith('arm:')) {
      const days = new Map();
      for (const p of line.raw) {
        if (!days.has(p.day)) days.set(p.day, []);
        days.get(p.day).push(p);
      }
      points = [...days.entries()].map(([day, ps]) => {
        const values = ps.map((p) => p.value);
        return {
          t: day * DAY + DAY / 2, value: values.reduce((a, b) => a + b, 0) / values.length,
          lo: Math.min(...values), hi: Math.max(...values), n: values.length,
          captureIds: [...new Set(ps.map((p) => p.captureId))], lids: [...new Set(ps.map((p) => p.lid))],
        };
      });
    } else {
      // One point per capture: two of its ROIs on one unit are their mean and range.
      const byCapture = new Map();
      for (const p of line.raw) {
        if (!byCapture.has(p.captureId)) byCapture.set(p.captureId, []);
        byCapture.get(p.captureId).push(p);
      }
      points = [...byCapture.values()].map((ps) => {
        const values = ps.map((p) => p.value);
        return { t: ps[0].t, value: values.reduce((a, b) => a + b, 0) / values.length, lo: Math.min(...values),
          hi: Math.max(...values), n: values.length, captureIds: [ps[0].captureId], lids: [ps[0].lid] };
      });
    }
    points.sort((a, b) => a.t - b.t);
    out.push({ key: line.key, name: line.name, points });
  }
  out.sort((a, b) => (a.key === EXPERIMENT_KEY) - (b.key === EXPERIMENT_KEY)
    || String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  return out;
}

export function lidStates(series) {
  const states = new Set();
  for (const s of series || []) for (const p of s.points) for (const l of p.lids) states.add(l || 'unknown');
  return [...states].sort();
}

// ── per target ─────────────────────────────────────────────────────────────
/** The order a target's lines come in: the isolate, its origin, its lab work (thermal.py LINK_ORDER). */
export const TARGET_ORDER = Object.freeze(['specimen', 'sample', 'external', 'plate', 'slant']);
const TARGET_METRICS = { t_avg: 't_avg_c', t_min: 't_min_c', t_max: 't_max_c', delta: 'delta_avg_c' };

export const targetKey = (target) => (target ? `${target.kind}:${target.id}` : null);

/** When a row's capture was taken, else when it was registered. */
export function rowTime(row) {
  const t = Date.parse(row?.captured_at || row?.registered_at || '');
  return Number.isFinite(t) ? t : null;
}

export function targetRows(captures) {
  const rows = [];
  for (const c of captures || []) {
    for (const r of c.analysis?.rois || []) {
      if (!r.target || r.redacted) continue;
      rows.push({ ...r, capture_id: c.capture_id, analysis_id: c.analysis.analysis_id,
        captured_at: c.captured_at || null, registered_at: c.created_at || null, lid_state: c.lid_state || null });
    }
  }
  return rows;
}

export function targetSeries(rows, { metric = 't_avg', target = null } = {}) {
  const field = TARGET_METRICS[metric];
  if (!field) throw new Error(`metric is one of ${Object.keys(TARGET_METRICS).join(', ')}`);
  const lines = new Map();
  for (const row of rows || []) {
    const key = targetKey(row.target);
    const t = rowTime(row);
    const value = row[field];
    if (!key || row.redacted || (target && key !== target) || t === null || !Number.isFinite(value)) continue;
    if (!lines.has(key)) {
      lines.set(key, { key, kind: row.target.kind, id: row.target.id, code: row.target.code || null,
        captures: new Map() });
    }
    const line = lines.get(key);
    if (!line.captures.has(row.capture_id)) line.captures.set(row.capture_id, { t, rows: [] });
    line.captures.get(row.capture_id).rows.push({ value, label: row.label, lid: row.lid_state || null });
  }
  const out = [];
  for (const line of lines.values()) {
    const points = [...line.captures.entries()].map(([captureId, { t, rows: rs }]) => {
      const values = rs.map((r) => r.value);
      return {
        t, value: values.reduce((a, b) => a + b, 0) / values.length, lo: Math.min(...values),
        hi: Math.max(...values), n: values.length, captureIds: [captureId],
        labels: rs.map((r) => r.label), lids: [...new Set(rs.map((r) => r.lid))],
      };
    });
    points.sort((a, b) => a.t - b.t || a.captureIds[0].localeCompare(b.captureIds[0]));
    out.push({ key: line.key, kind: line.kind, id: line.id, code: line.code, points });
  }
  out.sort((a, b) => TARGET_ORDER.indexOf(a.kind) - TARGET_ORDER.indexOf(b.kind)
    || String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true })
    || a.id.localeCompare(b.id));
  return out;
}
